import { Theme } from '@mui/material/styles';
import { SiteMeta } from '@src/types/SiteMeta';
import { TimeMode } from '@types';
import {
  formatChartDate,
  getChartXAxisFormatter,
  getHighchartsTimeConfig,
} from '@utils/timezoneUtils';
import dayjs from 'dayjs';
import Highcharts from 'highcharts';
import { createElement } from 'react';
import { renderToString } from 'react-dom/server';

import { TEMPERATURE_THRESHOLDS } from '../../types';
import { getTemperatureInfo } from '../../utils/temperatureUtils';
import { ChartTooltip } from './ChartTooltip';
import { chartStyles } from './styles';

export interface ChartConfigParams {
  theme: Theme;
  chartData: Array<[number, number | null]>;
  deviceName: string;
  rackId: string;
  currentTemperature?: number | null;
  yAxisMax: number;
  currentTimeZoneString: string;
  tzOffset: number;
  dateFrom: string;
  dateTo: string;
  interval: { value: number; unit: string };
  incidentPoint: [number, number] | null;
  siteMeta?: SiteMeta;
  timeMode: TimeMode;
}

export function createTemperaturePlotLines(
  theme: Theme,
): Highcharts.YAxisPlotLinesOptions[] {
  return [
    {
      value: TEMPERATURE_THRESHOLDS.INCIDENT,
      color: theme.palette.warning.main,
      dashStyle: 'ShortDash',
      width: 2,
      label: {
        text: 'Incident Threshold (40°C)',
        align: 'left',
        x: 10,
        style: chartStyles.plotLineLabelStyle(theme.palette.warning.main),
      },
      zIndex: 3,
    },
    {
      value: TEMPERATURE_THRESHOLDS.CRITICAL,
      color: theme.palette.error.main,
      dashStyle: 'ShortDash',
      width: 2,
      label: {
        text: 'Critical (50°C)',
        align: 'left',
        x: 10,
        style: chartStyles.plotLineLabelStyle(theme.palette.error.main),
      },
      zIndex: 3,
    },
  ];
}

export function createTemperatureSeries(
  chartData: Array<[number, number | null]>,
  deviceName: string,
  theme: Theme,
): Highcharts.SeriesSplineOptions {
  return {
    type: 'spline',
    name: `${deviceName} - Max Cell Temperature`,
    data: chartData,
    id: 'temperature-series',
    findNearestPointBy: 'x',
    lineWidth: 2,
    color: theme.palette.primary.main,
    connectNulls: false,
    marker: {
      enabled: false,
      states: {
        hover: chartStyles.markerHoverState(theme),
      },
    },
    states: {
      hover: {
        lineWidthPlus: 1,
        halo: {
          size: 0,
        },
      },
    },
    zones: [
      {
        value: TEMPERATURE_THRESHOLDS.INCIDENT,
        color: theme.palette.info.main,
      },
      {
        value: TEMPERATURE_THRESHOLDS.CRITICAL,
        color: theme.palette.warning.main,
      },
      {
        color: theme.palette.error.main,
      },
    ],
    zoneAxis: 'y',
  };
}

export function createIncidentMarkerSeries(
  incidentPoint: [number, number],
  theme: Theme,
): Highcharts.SeriesScatterOptions {
  return {
    type: 'scatter',
    name: 'Incident Point',
    data: [incidentPoint],
    color: theme.palette.error.main,
    marker: {
      ...chartStyles.scatterMarkerStyle(theme),
      symbol: 'triangle',
    },
    dataLabels: {
      enabled: true,
      format: `Incident: ${incidentPoint[1].toFixed(1)}°C`,
      style: chartStyles.dataLabelStyle(theme, theme.palette.error.main),
      y: -15,
      x: 0,
    },
    showInLegend: true,
    enableMouseTracking: true,
    zIndex: 6,
  };
}

export function createCurrentTempMarkerSeries(
  currentTemperature: number,
  dateTo: string,
  theme: Theme,
): Highcharts.SeriesScatterOptions {
  const tempInfo = getTemperatureInfo(currentTemperature, theme);
  const color = tempInfo?.color || theme.palette.success.main;

  return {
    type: 'scatter',
    name: 'Current Temperature',
    data: [[dayjs.utc(dateTo).valueOf(), currentTemperature]],
    color,
    marker: {
      ...chartStyles.scatterMarkerStyle(theme),
      symbol: 'diamond',
    },
    dataLabels: {
      enabled: true,
      format: `Current: ${currentTemperature.toFixed(1)}°C`,
      style: chartStyles.dataLabelStyle(theme, color),
      y: -15,
      x: 0,
    },
    showInLegend: true,
    enableMouseTracking: true,
    zIndex: 7,
  };
}

export function createChartOptions(
  params: ChartConfigParams,
): Highcharts.Options {
  const {
    theme,
    chartData,
    deviceName,
    rackId,
    currentTemperature,
    yAxisMax,
    currentTimeZoneString,
    tzOffset,
    dateFrom,
    dateTo,
    interval,
    incidentPoint,
    siteMeta,
    timeMode,
  } = params;

  const fromFormatted = siteMeta
    ? formatChartDate(dateFrom, 'MMM DD HH:mm', timeMode, siteMeta)
    : dayjs.utc(dateFrom).format('MMM DD HH:mm');
  const toFormatted = siteMeta
    ? formatChartDate(dateTo, 'MMM DD HH:mm', timeMode, siteMeta)
    : dayjs.utc(dateTo).format('MMM DD HH:mm');

  const subtitle = `${deviceName} - ${rackId} | ${fromFormatted} to ${toFormatted} | Interval: ${interval.value} ${interval.unit}${
    interval.value > 1 ? 's' : ''
  } | Points: ${chartData.length}`;

  const series: Highcharts.SeriesOptionsType[] = [
    createTemperatureSeries(chartData, deviceName, theme),
  ];

  if (incidentPoint) {
    series.push(createIncidentMarkerSeries(incidentPoint, theme));
  }

  if (
    currentTemperature !== undefined &&
    currentTemperature !== null &&
    !isNaN(currentTemperature)
  ) {
    series.push(
      createCurrentTempMarkerSeries(currentTemperature, dateTo, theme),
    );
  }

  return {
    boost: chartStyles.boostConfig(chartData.length),
    chart: {
      type: 'spline',
      height: chartStyles.chartConfig.height,
      zoomType: 'x',
      animation: chartStyles.chartConfig.animation,
      backgroundColor: chartStyles.chartConfig.backgroundColor,
    },
    title: {
      text: ' ',
      align: 'left',
    },
    subtitle: {
      text: subtitle,
      align: 'left',
      style: chartStyles.subtitleStyle(theme),
    },
    xAxis: {
      type: 'datetime',
      min: dayjs.utc(dateFrom).valueOf(),
      max: dayjs.utc(dateTo).valueOf() + 120 * 1000, // 2-minutes buffer for better visualization
      title: {
        text: `<span>Date/Time <span style="color:${theme.palette.text.secondary}">${currentTimeZoneString}</span></span>`,
        useHTML: true,
      },
      labels: {
        formatter: siteMeta
          ? getChartXAxisFormatter(timeMode, siteMeta)
          : function () {
              return dayjs(this.value).format('MMM DD HH:mm');
            },
        style: chartStyles.axisLabelStyle(theme),
      },
      crosshair: chartStyles.crosshairStyle(theme),
    },
    yAxis: {
      title: {
        text: 'Max Cell Temperature (°C)',
        style: chartStyles.axisTitleStyle(theme),
      },
      opposite: false,
      min: 0,
      max: yAxisMax,
      labels: {
        style: chartStyles.axisLabelStyle(theme),
      },
      gridLineColor: chartStyles.gridLineStyle(theme).color,
      gridLineWidth: chartStyles.gridLineStyle(theme).width,
      plotLines: createTemperaturePlotLines(theme),
    },
    tooltip: {
      enabled: true,
      shared: false,
      formatter: function (this: Highcharts.TooltipFormatterContextObject) {
        const point = this;
        const temp = point.y;
        const tempInfo = getTemperatureInfo(temp, theme);

        const formattedDate = siteMeta
          ? formatChartDate(point.x, 'MMM DD YYYY HH:mm:ss', timeMode, siteMeta)
          : dayjs(point.x).format('MMM DD YYYY HH:mm:ss');

        return renderToString(
          createElement(ChartTooltip, {
            formattedDate,
            temperature: temp,
            temperatureInfo: tempInfo,
            deviceName,
            rackId,
            theme,
          }),
        );
      },
      useHTML: true,
      ...chartStyles.tooltipStyle(theme),
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      series: {
        shadow: false,
        borderWidth: 0,
        states: {
          hover: {
            lineWidthPlus: 0,
          },
        },
        animation: chartStyles.seriesAnimation,
        turboThreshold: 0,
      },
    },
    series,
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
          },
          chartOptions: {
            yAxis: {
              labels: {
                enabled: false,
              },
              title: {
                text: null,
              },
            },
            plotOptions: {
              series: {
                dataLabels: {
                  enabled: false,
                },
              },
            },
          },
        },
      ],
    },
    time: getHighchartsTimeConfig(tzOffset),
  };
}
